import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { CalendarIcon, ArrowRightIcon, ClockIcon } from 'lucide-react';
const featuredPost = {
  title: 'Walk-In Urgent Care: What to Know Before Your Visit',
  excerpt: "From minor injuries to flu symptoms, our urgent care team is ready to see you — no appointment needed. Here's what to expect when you walk in to Healthy Choice Family Clinic.",
  date: 'February 28, 2026',
  readTime: '5 min read',
  category: 'Urgent Care',
  image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?w=1200&q=80'
};
const posts = [{
  title: 'Why Annual Physicals Matter — Even When You Feel Fine',
  excerpt: 'Preventive screenings catch problems early. Learn which tests our providers recommend based on your age, gender, and risk factors.',
  date: 'February 20, 2026',
  readTime: '4 min read',
  category: 'Wellness',
  image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80'
}, {
  title: 'Managing Diabetes: Tips from Our Internal Medicine Team',
  excerpt: "Living with diabetes doesn't have to be overwhelming. Our providers share practical advice on diet, medication, and monitoring.",
  date: 'February 12, 2026',
  readTime: '6 min read',
  category: 'Chronic Care',
  image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=600&q=80'
}, {
  title: 'Sliding Fee Scale: Healthcare You Can Afford',
  excerpt: 'No insurance? No problem. Our sliding fee scale ensures every patient receives quality care regardless of their financial situation.',
  date: 'February 5, 2026',
  readTime: '3 min read',
  category: 'Financial Access',
  image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&q=80'
}, {
  title: 'Back-to-School Immunizations: What Your Child Needs',
  excerpt: "Maryland requires specific vaccinations for school entry. Here's a complete checklist and how to schedule your child's visit.",
  date: 'January 28, 2026',
  readTime: '4 min read',
  category: 'Pediatrics',
  image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=600&q=80'
}, {
  title: 'Hypertension Awareness: Know Your Numbers',
  excerpt: 'High blood pressure is a silent killer. Our team explains what your numbers mean and how lifestyle changes can make a real difference.',
  date: 'January 20, 2026',
  readTime: '5 min read',
  category: 'Heart Health',
  image: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=600&q=80'
}, {
  title: 'New Extended Hours at Our Oxon Hill Location',
  excerpt: "We're now open until 7 PM on weekdays at our Oxon Hill office. Evening appointments available for primary and urgent care.",
  date: 'January 14, 2026',
  readTime: '2 min read',
  category: 'News',
  image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600&q=80'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function BlogPage() {
  return <>
      <PageHero eyebrow="Health & Wellness Blog" title="News, Tips & Community Updates" subtitle="Stay informed with the latest health news, wellness advice, and updates from Healthy Choice Family Clinic." data-id="element-246" />

      <section className="py-16 md:py-20 bg-white" aria-labelledby="featured-heading" data-id="element-247">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-248">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-teal-500 mb-8" data-id="element-249">
            Featured Story
          </p>
          <motion.a href="#" className="group flex flex-col lg:flex-row gap-8 bg-white border border-[#E5E9F0] rounded-[10px] overflow-hidden hover:shadow-lg transition-shadow duration-300" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-250">
            <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden" data-id="element-251">
              <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-252" />
            </div>
            <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center" data-id="element-253">
              <span className="inline-block text-xs font-bold uppercase tracking-wider text-white bg-teal-500 px-3 py-1 rounded-full mb-4 w-fit" data-id="element-254">
                {featuredPost.category}
              </span>
              <h2 id="featured-heading" className="text-2xl md:text-3xl font-bold text-navy-500 mb-4 group-hover:text-[#0A71B1] transition-colors" data-id="element-255">
                {featuredPost.title}
              </h2>
              <p className="text-base text-gray-500 leading-relaxed mb-6" data-id="element-256">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-400" data-id="element-257">
                <span className="flex items-center gap-1.5" data-id="element-258">
                  <CalendarIcon className="h-4 w-4" data-id="element-259" />
                  {featuredPost.date}
                </span>
                <span className="flex items-center gap-1.5" data-id="element-260">
                  <ClockIcon className="h-4 w-4" data-id="element-261" />
                  {featuredPost.readTime}
                </span>
              </div>
            </div>
          </motion.a>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gray-50" aria-labelledby="posts-heading" data-id="element-262">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-263">
          <h2 id="posts-heading" className="text-2xl md:text-3xl font-bold text-navy-500 mb-10" data-id="element-264">
            Latest Articles
          </h2>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-265">
            {posts.map(post => <motion.a key={post.title} href="#" variants={itemVariants} className="group bg-white border border-[#E5E9F0] rounded-[10px] overflow-hidden hover:shadow-md transition-shadow duration-300" data-id="element-266">
                <div className="h-48 overflow-hidden" data-id="element-267">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-268" />
                </div>
                <div className="p-6" data-id="element-269">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-teal-500 mb-3" data-id="element-270">
                    {post.category}
                  </span>
                  <h3 className="text-base font-bold text-navy-500 mb-2 group-hover:text-[#0A71B1] transition-colors leading-snug" data-id="element-271">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-2" data-id="element-272">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-273">
                    <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-274">
                      <CalendarIcon className="h-3.5 w-3.5" data-id="element-275" />
                      {post.date}
                    </span>
                    <span className="text-xs font-semibold text-navy-500 flex items-center gap-1 group-hover:text-[#0A71B1] transition-colors" data-id="element-276">
                      Read
                      <ArrowRightIcon className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-277" />
                    </span>
                  </div>
                </div>
              </motion.a>)}
          </motion.div>
        </div>
      </section>
    </>;
}